import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import api from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsRegistration, setNeedsRegistration] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [firebaseToken, setFirebaseToken] = useState(null);
  const retryCount = useRef(0);
  const sessionRestored = useRef(false);
  const MAX_RETRIES = 2;

  const verifyWithBackend = useCallback(async (fbUser) => {
    try {
      const fbToken = await fbUser.getIdToken(true);
      setFirebaseToken(fbToken);
      const res = await api.post('/auth/verify-token', { firebase_token: fbToken });
      retryCount.current = 0;
      setAuthError(null);
      if (res.data.exists) {
        localStorage.setItem('auth_token', res.data.token);
        setUser(res.data.user);
        setNeedsRegistration(false);
      } else {
        setUser(null);
        setNeedsRegistration(true);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'Verification failed';
      if (status === 401 && retryCount.current < MAX_RETRIES) {
        retryCount.current += 1;
        await new Promise(r => setTimeout(r, 1000));
        return verifyWithBackend(fbUser);
      }
      setAuthError(detail);
      setUser(null);
      if (status === 401) {
        setNeedsRegistration(true);
      }
    }
  }, []);

  // Restore session from backend JWT on mount
  useEffect(() => {
    const restoreSession = async () => {
      const existingToken = localStorage.getItem('auth_token');
      if (existingToken) {
        try {
          const res = await api.get('/profiles/me');
          setUser(res.data.user);
          setNeedsRegistration(false);
          sessionRestored.current = true;
        } catch {
          localStorage.removeItem('auth_token');
        }
      }
      setLoading(false);
    };

    restoreSession();

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      if (fbUser) {
        const existingToken = localStorage.getItem('auth_token');
        if (!existingToken && !sessionRestored.current) {
          retryCount.current = 0;
          await verifyWithBackend(fbUser);
          setLoading(false);
        }
      } else {
        if (!localStorage.getItem('auth_token') && !sessionRestored.current) {
          setUser(null);
          setNeedsRegistration(false);
          setAuthError(null);
          setLoading(false);
        }
      }
    });
    return () => unsubscribe();
  }, [verifyWithBackend]);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/profiles/me');
      setUser(res.data.user);
    } catch (e) {
      console.error('Failed to refresh user:', e);
    }
  }, []);

  const getFirebaseToken = useCallback(async () => {
    if (firebaseUser) {
      const token = await firebaseUser.getIdToken(true);
      setFirebaseToken(token);
      return token;
    }
    return firebaseToken;
  }, [firebaseUser, firebaseToken]);

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (e) { /* ignore */ }
    localStorage.removeItem('auth_token');
    sessionRestored.current = false;
    setUser(null);
    setFirebaseUser(null);
    setFirebaseToken(null);
    setNeedsRegistration(false);
    setAuthError(null);
  }, []);

  const retryVerification = useCallback(async () => {
    if (firebaseUser) {
      setAuthError(null);
      retryCount.current = 0;
      await verifyWithBackend(firebaseUser);
    }
  }, [firebaseUser, verifyWithBackend]);

  return (
    <AuthContext.Provider value={{ firebaseUser, user, setUser, loading, needsRegistration, setNeedsRegistration, authError, refreshUser, getFirebaseToken, logout, retryVerification }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be inside AuthProvider');
  return context;
}
